export interface ColumnMapping {
  csvColumn: string;
  targetField: string | null;
}

export interface ExpectedField {
  name: string;
  displayName: string;
  required: boolean;
  alternatives: string[];
}

export const EXPECTED_FIELDS: ExpectedField[] = [
  {
    name: 'externalContractId',
    displayName: 'External Contract ID',
    required: false,
    alternatives: ['External Contract ID', 'ExternalContractId', 'External_contract_id', 'external_contract_id']
  },
  {
    name: 'customerCode',
    displayName: 'Customer Code',
    required: true,
    alternatives: ['Customer Code', 'CustomerCode', 'Customer_code', 
      'customer_code', 'Account Code', 'AccountCode', 'Account_code', 'account_code']
  },
  {
    name: 'productCode',
    displayName: 'Product Code',
    required: true,
    alternatives: ['Product Code', 'ProductCode', 'Product_code', 
      'product_code', 'Stock Code', 'StockCode', 'Stock_code', 'stock_code']
  },
  {
    name: 'quantity',
    displayName: 'Quantity',
    required: true,
    alternatives: ['Quantity', 'quantity']
  },
  {
    name: 'price',
    displayName: 'Price',
    required: false,
    alternatives: ['Price', 'price', 'Rate', 'rate']
  },
  {
    name: 'net',
    displayName: 'Net',
    required: false,
    alternatives: ['Net', 'net', 'Net Total', 'NetTotal', 'Net_total', 'net_total', 'nettotal']
  },
  {
    name: 'comment',
    displayName: 'Comment',
    required: false,
    alternatives: ['Comment', 'comment']
  }
];

const normalizeHeader = (s: string): string =>
  s
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/_/g, '');

export function autoMapColumns(csvHeaders: string[]): Map<string, string> {
  const mappings = new Map<string, string>();
  
  csvHeaders.forEach(header => {
    const normalizedHeader = normalizeHeader(header);
    for (const field of EXPECTED_FIELDS) {
      if (field.alternatives.some(alt => normalizeHeader(alt) === normalizedHeader)) {
        mappings.set(header, field.name);
        break;
      }
    }
  });
  
  return mappings;
}

export function validateMappings(mappings: Map<string, string>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const mappedFields = new Set(mappings.values());
  
  const requiredFields = EXPECTED_FIELDS.filter(f => f.required);
  for (const field of requiredFields) {
    if (!mappedFields.has(field.name)) {
      errors.push(`Required field "${field.displayName}" is not mapped`);
    }
  }
  
  const targetFields = Array.from(mappings.values()).filter(v => v !== null);
  const uniqueTargets = new Set(targetFields);
  if (targetFields.length !== uniqueTargets.size) {
    errors.push('Some fields are mapped multiple times');
  }
  
  return { valid: errors.length === 0, errors };
}

export function toSnakeCase(str: string): string {
  return str
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .toLowerCase();
}
